import { TFunction } from 'i18next';
import { IMatter } from '../interface';
import { ITransactionRestrictions } from '../interface/transactionRestriction';
import { InvoiceableType } from '../constants/transaction';
import { AlertType, TransactionAlertSource } from '../interface/parameters';

export const getCustomerIdentificationRestrictionMessage = (t: TFunction) =>
  t('transaction:restrictions.requiredCustomerIdentificationsAreNotCompleted');

export const getServiceRestrictionMessage = (t: TFunction, scope: TransactionAlertSource) => {
  switch (scope) {
    case TransactionAlertSource.MATTER:
      return t('transaction:restrictions.matterHasServiceRestriction');
    case TransactionAlertSource.MATTERPRINCIPAL:
      return t('transaction:restrictions.matterPrincipalHasServiceRestriction');
    case TransactionAlertSource.MATTERPAYER:
      return t('transaction:restrictions.payerHasServiceRestriction');
  }
};

export const getOverDueInvoicesMessage = (
  t: TFunction,
  scope: TransactionAlertSource,
  minDaysInvoicesOverdueAlert: number,
) => {
  switch (scope) {
    case TransactionAlertSource.MATTER:
      return t('transaction:restrictions.matterHasInvoicesWhichAreOverDue', {
        minDaysInvoicesOverdueAlert,
      });
    case TransactionAlertSource.MATTERPRINCIPAL:
      return t('transaction:restrictions.prinicipalHasOverdueInvoices', {
        minDaysInvoicesOverdueAlert,
      });
    case TransactionAlertSource.MATTERPAYER:
      return t('transaction:restrictions.oneOrMorePayersInTheMatterHasOverdueInvoices', {
        minDaysInvoicesOverdueAlert,
      });
  }
};

/**
 * The warning-level restrictions of a matter, as messages to show inline under the matter field.
 *
 * Warnings do not block the user, so they are never raised as a dialog, the form states them and
 * lets the user carry on. Error-level restrictions are absent here on purpose: those still go
 * through the blocking dialogs in the restriction containers.
 *
 * Mirrors the order and the precedence of the checks in `useHandleTransactionRestrictions`:
 * internal matters are exempt from all of them, and overdue invoices are only reported when the
 * matter has no service restriction of its own.
 */
export const getMatterRestrictionWarnings = (
  t: TFunction,
  matter?: IMatter,
  restrictions?: ITransactionRestrictions,
): string[] => {
  if (!matter || !restrictions) return [];
  if (restrictions.matterId !== matter.id || matter.invoiceableType === InvoiceableType.INTERNAL) {
    return [];
  }

  const warnings: string[] = [];
  const { customerIdentification, serviceRestriction, overDueInvoices } = restrictions;

  if (customerIdentification.hasIssue && customerIdentification.alertType === AlertType.WARNING) {
    warnings.push(getCustomerIdentificationRestrictionMessage(t));
  }

  if (serviceRestriction.hasIssue) {
    if (serviceRestriction.alertType === AlertType.WARNING) {
      warnings.push(getServiceRestrictionMessage(t, serviceRestriction.scope));
    }
  } else if (overDueInvoices.hasIssue && overDueInvoices.alertType === AlertType.WARNING) {
    warnings.push(
      getOverDueInvoicesMessage(
        t,
        overDueInvoices.scope,
        overDueInvoices.minimumAllowedOverDueDaysBeforeAlert,
      ),
    );
  }

  return warnings;
};
